import React, { FC, useCallback, useMemo } from 'react';
import { SxProps, Theme } from '@mui/material';
import { clsx, SystemHeader, useStyles } from '@sweatpals/react-ui-kit';
import { useRouter } from 'next/router';
import { authStore } from '@/stores';
import { APP_PAGE_URL, ATTENDEE_ACCOUNT_PAGE_URL, AUTH_PAGE_URL, DASHBOARD_URL } from '../../constants';
import { BOOK_DEMO_URL } from '@sweatpals/react-web-shared';
import useLink from '@/modules/_core/hooks/useLink';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import { headerV3Styles } from '@/modules/_core/components/header/header.styles';

export interface HeaderV3Props {
	sx?: SxProps<Theme>;
}

const HeaderV3: FC<HeaderV3Props> = ({ sx }) => {
	const { user: currentUser, isAuthLoading } = authStore;
	const router = useRouter();
	const { makeSearchEventsLink } = useLink();
	const styles = useStyles(headerV3Styles);

	const isEventSearchPage = useMemo(() => router.pathname === '/search', [router.pathname]);

	const openEventSearchPage = useCallback(async (search?: string): Promise<void> => {
		if (isEventSearchPage) {
			await router.push(
				makeSearchEventsLink({
					...router.query,
					search,
				})
			);

			return;
		}

		await router.push(makeSearchEventsLink({ search }));
	}, [isEventSearchPage, makeSearchEventsLink, router]);
	const openCreateEventPage = useCallback((): void => {
		window.open(`/create/event`, '_self');
	}, []);
	const openDashboardPage = useCallback(() => {
		window.open(DASHBOARD_URL, '_self');
	}, []);
	const openCreateCommunityPage = useCallback(() => {
		window.open(`/create/community`, '_self');
	}, []);
	const openDownloadAppPage = useCallback(() => {
		window.open(APP_PAGE_URL);
	}, []);
	const openPurchasesPage = useCallback(() => {
		window.open(ATTENDEE_ACCOUNT_PAGE_URL, '_self');
	}, []);
	const openLoginPage = useCallback(() => {
		window.open(`${AUTH_PAGE_URL}?redirect=${window.location.href}`, '_self');
	}, []);
	const openBookDemoPage = useCallback(() => {
		window.open(BOOK_DEMO_URL, '_blank');
	}, []);

	return (
		<SystemHeader
			sx={clsx(styles.root, sx)}
			currentUser={currentUser}
			isLoading={isAuthLoading}
			handleLogout={() => authStore.logout()}
			getPublicFileUrl={(id, variant) => getPublicFileUrl(id, { variant })}
			openLoginPage={openLoginPage}
			openDashboardPage={openDashboardPage}
			openPurchasesPage={openPurchasesPage}
			openDownloadAppPage={openDownloadAppPage}
			openCreateCommunityPage={openCreateCommunityPage}
			openCreateEventPage={openCreateEventPage}
			openEventSearchPage={openEventSearchPage}
			openBookDemoPage={openBookDemoPage}
		/>
	);
};

export default HeaderV3;
